/**
 * Wire types for the Drive For Me admin API.
 *
 * These mirror the Java DTOs in `com.opriontravel.driveforme.dto.admin` and the
 * auth response one-for-one. Keeping them in a single file makes a backend
 * change a compile error here rather than a runtime surprise on a screen.
 */

/* -------------------------------------------------------------------- auth */

export interface AuthResponse {
  token: string
  userId: number
  name: string
  email: string | null
  phone: string | null
  role: string
  profileImageUrl: string | null
  enabled: boolean | null
  emailVerified: boolean | null
  phoneVerified: boolean | null
  requiresPhoneVerification: boolean | null
  createdAt: string | null
}

export interface LoginRequest {
  identifier: string
  password: string
}

/* -------------------------------------------------------------- pagination */

export interface PageResponse<T> {
  content: T[]
  page: number
  size: number
  totalElements: number
  totalPages: number
  first: boolean
  last: boolean
}

export type SortDirection = 'asc' | 'desc'

export interface PageParams {
  page?: number
  size?: number
  sort?: string
  direction?: SortDirection
}

/* ------------------------------------------------------------------ people */

export interface AdminUser {
  id: number
  name: string | null
  email: string | null
  phone: string | null
  profileImageUrl: string | null
  role: string | null
  authProvider: string | null
  enabled: boolean
  phoneVerified: boolean
  emailVerified: boolean
  createdAt: string | null
  updatedAt: string | null
}

export interface AdminDriver {
  id: number
  name: string | null
  email: string | null
  phone: string | null
  profileImageUrl: string | null
  enabled: boolean
  phoneVerified: boolean
  activeTripCount: number
  createdAt: string | null
  updatedAt: string | null
}

export interface DriverOption {
  id: number
  name: string | null
  phone: string | null
  profileImageUrl: string | null
  activeTripCount: number
}

export interface PeopleFilters extends PageParams {
  search?: string
  enabled?: boolean
  phoneVerified?: boolean
  createdFrom?: string
  createdTo?: string
}

/* ---------------------------------------------------------- trip requests */

export const TRIP_REQUEST_STATUSES = ['PENDING', 'CONFIRMED', 'COMPLETED', 'CANCELLED'] as const
export type TripRequestStatus = (typeof TRIP_REQUEST_STATUSES)[number]

export const SERVICE_TYPES = ['CITY', 'TRAVEL'] as const
export type ServiceType = (typeof SERVICE_TYPES)[number]

export const REQUEST_PAYMENT_STATUSES = ['UNPAID', 'PAID', 'REFUNDED', 'FAILED', 'PENDING_PAYMENT'] as const
export type RequestPaymentStatus = (typeof REQUEST_PAYMENT_STATUSES)[number]

/* ---------------------------------------------------------------- payments */

export const PAYMENT_METHODS = ['CARD', 'CASH', 'INSTAPAY'] as const
export type PaymentMethodCode = (typeof PAYMENT_METHODS)[number]

export const PAYMENT_VERIFICATION_STATUSES = [
  'NOT_REQUIRED',
  'PENDING',
  'VERIFIED',
  'REJECTED',
] as const
export type PaymentVerificationStatus = (typeof PAYMENT_VERIFICATION_STATUSES)[number]

/**
 * How one booking was paid for. Nested identically on trips and trip requests,
 * so both screens render it through the same component and cannot drift.
 *
 * Every amount arrives twice: `*Minor` is the integer piastre figure the server
 * computed, and the plain field is the same value as a decimal for display. The
 * dashboard never adds or subtracts money — if a figure is needed, the server
 * has already sent it.
 *
 * All fields tolerate a booking made before any of this existed: `pointsUsed`
 * is zero on those rows, `externalAmount` is the whole charge, and
 * `verificationStatus` is `NOT_REQUIRED`.
 */
export interface AdminPaymentSummary {
  /** The raw stored value, which may be any historical spelling. Do not switch on it. */
  paymentMethod: string | null
  /** The stable machine value. Null for a value the backend has never written. */
  paymentMethodCode: PaymentMethodCode | null
  paymentStatus: string | null

  totalAmountMinor: number
  tripAmountMinor: number
  penaltyAmountMinor: number
  pointsUsedMinor: number
  externalAmountMinor: number

  totalAmount: number
  tripAmount: number
  penaltyAmount: number
  pointsUsed: number
  externalAmount: number

  currency: string

  fullyCoveredByPoints: boolean
  partiallyCoveredByPoints: boolean

  verificationStatus: PaymentVerificationStatus | null
  verificationComment: string | null
  verifiedByAdminUserId: number | null
  verifiedByAdminName: string | null
  verifiedAt: string | null
  /**
   * Whether a manual decision is permitted. False for card, whose status is the
   * gateway's. The server enforces the same rule, so this only decides what is
   * rendered — it never authorises anything.
   */
  canVerify: boolean

  hasProof: boolean
  proofUploadedAt: string | null
  proofContentType: string | null
  proofSizeBytes: number | null

  /**
   * Whether an operator may switch this booking from credit card to Instapay.
   *
   * True only for a live card booking whose payment is still pending and which
   * carries no gateway transaction — the same conditions the endpoint enforces.
   * Computed on the server for the usual reason: the dashboard offers the action
   * on exactly the rows the server would accept it on, rather than working the
   * rule out again from a status string and drifting.
   */
  canConvertToInstapay: boolean
  /**
   * Whether an operator may attach a proof of payment themselves.
   *
   * False for card, which has no receipt to file against it. True wherever a
   * human settles the payment — the same rule as {@link canVerify}, and the two
   * move together.
   */
  canUploadProof: boolean
}

/** The same summary, fetched on its own for the verification panel. */
export interface PaymentVerificationDetails extends AdminPaymentSummary {
  tripRequestId: number
  bookingReference: string | null
}

/** One movement of a customer's points, as the details drawer lists them. */
export interface PointsTransaction {
  id: number
  userId: number
  /** Signed: positive credited the customer, negative spent. */
  amountMinor: number
  balanceAfterMinor: number
  amount: number
  balanceAfter: number
  currency: string
  type: 'REFUND_CREDIT' | 'BOOKING_REDEMPTION' | 'BOOKING_RESTORE' | 'ADMIN_ADJUSTMENT' | null
  credit: boolean
  tripRequestId: number | null
  tripId: number | null
  bookingReference: string | null
  paymentRefundId: number | null
  reason: string | null
  createdByAdminUserId: number | null
  createdByAdminName: string | null
  createdAt: string | null
}

export interface InstapayConfig {
  id: number | null
  bankName: string | null
  accountNumber: string | null
  accountHolderName: string | null
  instructions: string | null
  active: boolean
  /** False until every required field is filled in. The app hides Instapay until it is true. */
  usable: boolean
  updatedByAdminUserId: number | null
  updatedByAdminName: string | null
  updatedAt: string | null
}

export interface InstapayConfigUpdate {
  bankName: string
  accountNumber: string
  accountHolderName: string
  instructions?: string | null
  active: boolean
}

export interface PaymentVerificationRequest {
  comment?: string
}

/**
 * An operator switching how a booking is paid for.
 *
 * Carries the target method and why, and deliberately not the method being moved
 * *from*: the server reads that off the booking, so a stale row on screen cannot
 * talk it into a conversion it would otherwise refuse.
 *
 * `INSTAPAY` is the only value the API accepts today — see
 * `changePaymentMethod` for why the other directions are not supported.
 */
export interface PaymentMethodChangeRequest {
  method: PaymentMethodCode
  reason?: string
}

/** What the API answers after a proof image is stored. */
export interface PaymentProofUploadResponse {
  success: boolean
  tripRequestId: number
  /** Detected from the file's own bytes, not from what the browser declared. */
  contentType: string
  sizeBytes: number
  /** Always `PENDING`: an upload is evidence, not a payment. */
  verificationStatus: PaymentVerificationStatus
  message: string
}

export interface AdminTripRequest {
  id: number
  bookingReference: string | null
  userId: number | null
  userName: string | null
  userPhone: string | null
  driverId: number | null
  driverName: string | null
  driverPhone: string | null
  categoryName: string | null
  serviceType: string | null
  packageName: string | null
  pickupAddress: string | null
  /**
   * Where the journey actually is, for the route map.
   *
   * Null when the booking never carried a coordinate — the drop-off half is
   * empty on open-ended TRAVEL bookings, and both halves are on rows written
   * before the app sent them. The server sends null rather than the 0.0 its
   * primitive columns hold, so an unset pair cannot be mistaken for a real
   * point in the Atlantic.
   */
  pickupLatitude: number | null
  pickupLongitude: number | null
  dropoffAddress: string | null
  dropoffLatitude: number | null
  dropoffLongitude: number | null
  status: string | null
  paymentStatus: string | null
  paymentMethod: string | null
  basePrice: number | null
  carModel: string | null
  transmissionMode: string | null
  startTime: string | null
  endTime: string | null
  scheduledAt: string | null
  createdAt: string | null
  /** The Paymob charge behind this booking, when there is one. */
  paymobTransactionId: number | null
  /**
   * Cheap hint that a refund is worth asking about — it does not authorise one.
   * The Refund action is still gated on the server's eligibility endpoint.
   */
  refundCandidate: boolean
  /**
   * Whether to offer the Cancel action. True for a confirmed booking as well as
   * a pending one — the confirmed ones with a driver already assigned are
   * exactly the bookings customers ring up to cancel.
   */
  canCancel: boolean
  /** Whether the pickup and finish times may still be moved. */
  canModifySchedule: boolean
  /**
   * Why this booking's payment stops a driver being assigned, or null when
   * nothing does.
   *
   * A sentence rather than a boolean, and the same sentence the API throws if
   * the assignment is attempted anyway. An operator looking at a booking they
   * cannot dispatch needs to know which reason it is, because each has a
   * different fix: an Instapay booking wants its receipt attached, a pending
   * card booking wants switching to Instapay, and a refunded one wants leaving
   * alone.
   *
   * Says nothing about whether the request is still pending — that is `status`,
   * which is the existing gate on the Approve action. This covers the payment.
   */
  assignmentBlockedReason: string | null
  /** How this booking was paid for. Always present, including on legacy rows. */
  payment: AdminPaymentSummary
}

export interface TripRequestFilters extends PageParams {
  search?: string
  status?: string
  serviceType?: string
  paymentStatus?: string
  paymentMethod?: string
  verificationStatus?: string
  from?: string
  to?: string
}

/* ------------------------------------------------------------------- trips */

export const TRIP_STATUSES = [
  'PENDING',
  'CONFIRMED',
  'ON_THE_WAY',
  'ARRIVED',
  'STARTED',
  'COMPLETED',
  'CANCELLED',
] as const
export type TripStatus = (typeof TRIP_STATUSES)[number]

export interface AdminTrip {
  id: number
  requestId: number | null
  bookingReference: string | null
  userId: number | null
  userName: string | null
  userPhone: string | null
  driverId: number | null
  driverName: string | null
  driverPhone: string | null
  driverImageUrl: string | null
  categoryName: string | null
  serviceType: string | null
  packageName: string | null
  pickupAddress: string | null
  pickupLatitude: number | null
  pickupLongitude: number | null
  dropoffAddress: string | null
  dropoffLatitude: number | null
  dropoffLongitude: number | null
  status: string | null
  startTime: string | null
  startedAt: string | null
  endTime: string | null
  endedAt: string | null
  driverArrivedAt: string | null
  createdAt: string | null
  carModel: string | null
  transmissionMode: string | null
  paymentMethod: string | null
  basePaymentStatus: string | null
  extraPaymentStatus: string | null
  basePrice: number | null
  extraHoursCost: number | null
  totalPrice: number | null
  extraHoursCount: number | null
  /** Server-computed lifecycle permissions — the dashboard never guesses. */
  canStart: boolean
  canEnd: boolean
  canModifyDriver: boolean
  canModifyTimes: boolean
  /** False once the journey is complete or already called off. */
  canCancel: boolean
  /** The Paymob charge behind this trip's booking, when there is one. */
  paymobTransactionId: number | null
  /** Whether to offer a Refund action. The eligibility endpoint is still the authority. */
  refundCandidate: boolean
  /**
   * How the booking behind this trip was paid for — the same record the Trip
   * Requests page renders, read from the same booking row. A trip has no
   * payment of its own.
   */
  payment: AdminPaymentSummary
}

export interface TripFilters extends PageParams {
  search?: string
  status?: string
  driverId?: number
  paymentMethod?: string
  verificationStatus?: string
  from?: string
  to?: string
}

/* ------------------------------------------------------------- promo codes */

export const DISCOUNT_TYPES = ['PERCENTAGE', 'FIXED'] as const
export type DiscountType = (typeof DISCOUNT_TYPES)[number]

export const PROMO_STATES = ['ACTIVE', 'SCHEDULED', 'EXPIRED', 'DISABLED'] as const
export type PromoState = (typeof PROMO_STATES)[number]

export interface AdminPromo {
  id: number
  code: string
  description: string | null
  discountType: string | null
  discountValue: number | null
  maxDiscountAmount: number | null
  usageLimitTotal: number | null
  usageLimitPerUser: number | null
  usedCount: number | null
  startDate: string | null
  endDate: string | null
  isActive: boolean
  state: string
  createdAt: string | null
  updatedAt: string | null
}

export interface PromoFilters extends PageParams {
  search?: string
  state?: string
  discountType?: string
}

/** Field names match the existing `/api/admin/promo/add` contract. */
export interface PromoCreateRequest {
  code: string
  description: string
  discountType: 'percentage' | 'fixed'
  discountValue: number
  maxDiscountValue: number
  usageLimitTotal: number
  usageLimitPerUser: number
  startDate: string
  endDate: string
}

export interface PromoUpdateRequest extends PromoCreateRequest {
  id: number
  isActive: boolean
}

/* --------------------------------------------------------------- dashboard */

export interface TimeSeriesPoint {
  date: string
  count: number
}

export interface DashboardStats {
  totalUsers: number
  activeUsers: number
  suspendedUsers: number
  totalDrivers: number
  activeDrivers: number
  suspendedDrivers: number
  pendingTripRequests: number
  confirmedTripRequests: number
  cancelledTripRequests: number
  activeTrips: number
  completedTrips: number
  cancelledTrips: number
  totalTrips: number
  activePromoCodes: number
  scheduledPromoCodes: number
  expiredPromoCodes: number
  disabledPromoCodes: number
  tripsByStatus: Record<string, number>
  tripRequestsByStatus: Record<string, number>
  tripsPerDay: TimeSeriesPoint[]
  signupsPerDay: TimeSeriesPoint[]
}

export const ACTIVITY_TYPES = ['TRIP', 'TRIP_REQUEST'] as const
export type ActivityType = (typeof ACTIVITY_TYPES)[number]

export interface ActivityEntry {
  type: ActivityType
  referenceId: number
  bookingReference: string | null
  title: string | null
  subtitle: string | null
  status: string | null
  occurredAt: string
}

/* --------------------------------------------------------------- reporting */

/** One bucket of the outcome trend; `date` is the day, or the first of the month. */
export interface ReportTrendPoint {
  date: string
  received: number
  fulfilled: number
  cancelled: number
  active: number
}

export interface ReportServiceTypeStat {
  type: ServiceType
  received: number
  fulfilled: number
  cancelled: number
}

/** All money in integer minor units (EGP piastres) — divide by 100 only to display. */
export interface BookingReportMoney {
  fulfilledRevenueMinor: number
  cashRevenueMinor: number
  cardRevenueMinor: number
  refundsPaidMinor: number
  refundsCount: number
  outstandingPenaltiesMinor: number
  outstandingPenaltiesCount: number
  pointsSpentMinor: number
}

export const REPORT_TREND_GRANULARITIES = ['DAY', 'MONTH'] as const
export type ReportTrendGranularity = (typeof REPORT_TREND_GRANULARITIES)[number]

/** One band of the overall response-time histogram, e.g. `{ label: '< 5m', count: 12 }`. */
export interface ResponseTimeBucket {
  label: string
  count: number
}

/**
 * One employee's answered requests in the window. `adminUserId`/`adminName` are
 * null only for the rare unattributed row; `averageSeconds`/`medianSeconds` are
 * null when they answered nothing (which never happens for a row that exists).
 */
export interface EmployeeResponseStat {
  adminUserId: number | null
  adminName: string | null
  respondedCount: number
  assignedCount: number
  rejectedCount: number
  averageSeconds: number | null
  medianSeconds: number | null
}

/**
 * How quickly requests were answered — a driver assigned, or a pending request
 * rejected by a supervisor — overall and per employee.
 *
 * All durations are whole **seconds**; the browser formats them and never does
 * arithmetic. Unlike the rest of {@link BookingReport} this is a cohort on
 * **when each request was answered**, not when the booking was received, and it
 * is grouped by the employee who answered. `respondedCount` equals
 * `assignedCount + rejectedCount` and the sum of every employee's
 * `respondedCount`. The averages are null when nothing was answered in the
 * window.
 */
export interface ResponseTimeReport {
  respondedCount: number
  assignedCount: number
  rejectedCount: number
  averageSeconds: number | null
  medianSeconds: number | null
  p90Seconds: number | null
  distribution: ResponseTimeBucket[]
  perEmployee: EmployeeResponseStat[]
}

/**
 * The Reporting screen's payload. `from` is null for an all-time report; the
 * outcome counts reconcile as `received == fulfilled + cancelledTotal + active`,
 * and the three cancellation figures sum to `cancelledTotal`.
 */
export interface BookingReport {
  from: string | null
  to: string
  received: number
  fulfilled: number
  active: number
  confirmed: number
  cancelledTotal: number
  cancelledByCustomer: number
  cancelledByStaff: number
  cancelledBySystem: number
  fulfillmentRate: number
  cancellationRate: number
  serviceTypes: ReportServiceTypeStat[]
  trendGranularity: ReportTrendGranularity
  trend: ReportTrendPoint[]
  money: BookingReportMoney
  responseTime: ResponseTimeReport
}

/* ----------------------------------------------------------------- refunds */

export const REFUND_STATUSES = [
  'REQUESTED',
  'PROCESSING',
  'SUCCEEDED',
  'FAILED',
  'PENDING',
  'REQUIRES_RECONCILIATION',
] as const
export type RefundStatus = (typeof REFUND_STATUSES)[number]

export const REFUND_SOURCES = ['MANUAL_ADMIN', 'TRIP_CANCELLATION', 'CARD_VERIFICATION'] as const
export type RefundSource = (typeof REFUND_SOURCES)[number]

/**
 * Stable machine codes the API returns when a refund is refused.
 * Mirrors `RefundException.Code` on the backend.
 */
export const REFUND_ERROR_CODES = [
  'NOT_REFUNDABLE',
  'ALREADY_FULLY_REFUNDED',
  'REFUND_ALREADY_PROCESSING',
  'AMOUNT_EXCEEDS_REMAINING',
  'INVALID_AMOUNT',
  'INVALID_REASON',
  'UNSUPPORTED_PAYMENT_METHOD',
  'MISSING_TRANSACTION_ID',
  'PAYMENT_NOT_CAPTURED',
  'PAYMOB_REJECTED',
  'GATEWAY_UNCERTAIN',
  'RECORD_NOT_FOUND',
  'UNAUTHORIZED',
  'FORBIDDEN',
] as const
export type RefundErrorCode = (typeof REFUND_ERROR_CODES)[number]

/**
 * One refund attempt.
 *
 * Amounts are integer minor units (EGP piastres), exactly as stored and as sent
 * to Paymob. The browser formats them; it never does the arithmetic, so no
 * rounding can creep in between the ledger and the screen.
 */
export interface AdminRefund {
  id: number
  idempotencyKey: string

  paymobTransactionId: number | null
  paymobOrderId: number | null
  paymobRefundTransactionId: number | null

  tripRequestId: number | null
  tripId: number | null
  bookingReference: string | null
  customerUserId: number | null
  customerName: string | null

  originalAmountMinor: number
  requestedAmountMinor: number
  refundedAmountMinor: number | null
  currency: string
  paymentMethod: string | null

  reason: string
  adminNote: string | null
  source: string | null
  status: string | null

  adminUserId: number | null
  adminName: string | null
  adminEmail: string | null

  attemptCount: number
  gatewayHttpStatus: number | null
  gatewayResponseCode: string | null
  gatewayMessage: string | null
  gatewayMetadata: string | null

  createdAt: string | null
  updatedAt: string | null
  requestedAt: string | null
  completedAt: string | null
  lastReconciledAt: string | null

  /** True while the attempt still needs a human or a callback to settle it. */
  needsAttention: boolean
}

export interface RefundFilters extends PageParams {
  search?: string
  status?: string
  source?: string
  adminUserId?: number
  paymobTransactionId?: number
  bookingReference?: string
  from?: string
  to?: string
}

export interface RefundSummary {
  succeededAmountMinor: number
  succeededCount: number
  pendingAmountMinor: number
  pendingCount: number
  failedAmountMinor: number
  failedCount: number
  needsAttentionCount: number
  totalCount: number
  currency: string
}

/**
 * The server's verdict on whether a booking can be refunded.
 * The dashboard never derives this from status strings of its own.
 */
export interface RefundEligibility {
  canRefund: boolean
  refundBlockedReason: string | null
  refundBlockedCode: string | null

  tripRequestId: number | null
  tripId: number | null
  bookingReference: string | null
  customerUserId: number | null
  customerName: string | null
  customerPhone: string | null

  paymobTransactionId: number | null
  paymobOrderId: number | null
  paymentMethod: string | null
  paymentStatus: string | null
  currency: string

  /**
   * What the customer actually paid outside points:
   * `tripAmountMinor + settledPenaltyAmountMinor - pointsUsedAmountMinor`.
   *
   * This is the refundable ceiling. Points were never money leaving the
   * customer's bank, so returning them is the separate restore path and
   * counting them here would let an operator refund value that was never
   * transferred.
   */
  originalPaidAmountMinor: number
  /**
   * The fare alone, after any promo discount. The cancellation policy's
   * percentage applies to this rather than to the total — a booking that also
   * cleared an old cancellation penalty must not have that debt handed back by
   * an automatic tier.
   */
  tripAmountMinor: number
  /** Zero for every booking that did not clear an earlier penalty, which is almost all of them. */
  settledPenaltyAmountMinor: number
  /** Zero for every booking made before points existed. */
  pointsUsedAmountMinor: number
  successfullyRefundedAmountMinor: number
  pendingRefundAmountMinor: number
  remainingRefundableAmountMinor: number

  /**
   * How an approved refund actually reaches the customer.
   *
   * `GATEWAY` reverses a Paymob charge. `POINTS` credits application points,
   * which is the only way to return an Instapay transfer — a bank transfer
   * cannot be sent back the way it arrived. `NONE` when nothing is refundable.
   *
   * The dialog words its confirmation from this, so a supervisor is told the
   * customer will receive points *before* they approve rather than after.
   */
  refundSettlementChannel: RefundSettlementChannel
  partialRefundSupported: boolean
  existingRefunds: AdminRefund[]
}

export const REFUND_SETTLEMENT_CHANNELS = ['GATEWAY', 'POINTS', 'NONE'] as const
export type RefundSettlementChannel = (typeof REFUND_SETTLEMENT_CHANNELS)[number]

/* ----------------------------------------------------------- cancellation */

export const CANCELLATION_BANDS = ['FULL', 'HALF', 'NONE'] as const
export type CancellationBand = (typeof CANCELLATION_BANDS)[number]

export type RefundMode = 'FULL' | 'PARTIAL' | 'NONE'
export type PenaltyMode = 'APPLY' | 'NONE'

/**
 * The server's answer to "what should this cancellation dialog look like, and
 * what should it default to?".
 *
 * `mode` decides the dialog's shape and is deliberately not a payment-method
 * string: a booking marked CARD whose payment never captured has nothing to
 * refund, and offering to refund it would offer something the server would then
 * refuse.
 */
export interface CancellationOptions {
  mode: 'REFUND' | 'PENALTY'
  cancellable: boolean
  blockedReason: string | null

  tripRequestId: number | null
  tripId: number | null
  bookingReference: string | null
  customerUserId: number | null
  customerName: string | null
  customerPhone: string | null
  serviceType: string | null
  paymentMethod: string | null
  paymentStatus: string | null
  currency: string

  scheduledPickupAt: string | null
  minutesUntilPickup: number | null
  /** False when the booking has no recorded pickup time — say so rather than showing a window. */
  scheduleKnown: boolean

  policyBand: CancellationBand
  policyRefundPercent: number
  /** What the policy would refund — the labelled default for a card booking. */
  policyRefundMinor: number
  /** What the policy would charge — the labelled default for a cash booking. */
  policyPenaltyMinor: number

  tripAmountMinor: number
  settledPenaltyAmountMinor: number
  originalPaidAmountMinor: number
  alreadyRefundedMinor: number
  pendingRefundMinor: number
  remainingRefundableMinor: number
  partialRefundSupported: boolean
}

/* ------------------------------------------------------------- rescheduling */

/**
 * An operator's edit to a booking's times.
 *
 * A field left out is one they did not touch, and the server leaves it alone —
 * so moving only the pickup does not require echoing back an end time. Both
 * values are Cairo wall clocks ("2026-08-14T10:30"), the same convention the
 * rest of the API uses.
 */
export interface RescheduleBookingRequest {
  startTime?: string
  endTime?: string
  reason?: string
}

/* --------------------------------------------------------- change history */

export const BOOKING_CHANGE_ACTIONS = ['SCHEDULE_CHANGED', 'DRIVER_REASSIGNED', 'CANCELLED'] as const
export type BookingChangeAction = (typeof BOOKING_CHANGE_ACTIONS)[number]

export const BOOKING_CHANGE_FIELDS = ['START_TIME', 'END_TIME', 'DRIVER', 'STATUS'] as const
export type BookingChangeField = (typeof BOOKING_CHANGE_FIELDS)[number]

/**
 * One operator change to a booking.
 *
 * `oldValue` and `newValue` arrive already formatted by the server and are
 * printed as they are — a time in this list is the operational wall clock the
 * operator saw, not an instant to re-render.
 */
export interface BookingChangeLogEntry {
  id: number
  tripRequestId: number | null
  tripId: number | null
  bookingReference: string | null
  action: string | null
  field: string | null
  oldValue: string | null
  newValue: string | null
  summary: string | null
  /** What the operator typed, where the action asked for a reason. */
  reason: string | null
  adminUserId: number | null
  adminName: string | null
  createdAt: string | null
}

export interface CancelBookingRequest {
  refundMode: RefundMode
  /** Minor units. Required when `refundMode` is `PARTIAL`. */
  refundAmountMinor?: number
  penaltyMode: PenaltyMode
  /** Minor units. Required when `penaltyMode` is `APPLY`. */
  penaltyAmountMinor?: number
  reason: string
}

/* --------------------------------------------------------------- penalties */

export const PENALTY_STATUSES = ['OUTSTANDING', 'PENDING_SETTLEMENT', 'SETTLED', 'WAIVED'] as const
export type PenaltyStatus = (typeof PENALTY_STATUSES)[number]

export const PENALTY_SOURCES = ['CUSTOMER_CANCELLATION', 'ADMIN_CANCELLATION'] as const
export type PenaltySource = (typeof PENALTY_SOURCES)[number]

/**
 * One cancellation penalty.
 *
 * Carries its whole explanation, not just an amount: which booking it came
 * from, which band produced it and how long before pickup the customer
 * cancelled. Operators get asked "why am I being charged 250?" and need to be
 * able to answer it.
 */
export interface CustomerPenalty {
  id: number
  userId: number | null
  customerName: string | null
  customerPhone: string | null

  amountMinor: number
  currency: string
  status: PenaltyStatus | null

  sourceTripRequestId: number | null
  sourceTripId: number | null
  sourceBookingReference: string | null
  bookingPriceMinor: number
  serviceType: string | null
  policyBand: string | null
  refundPercent: number
  minutesBeforePickup: number | null
  scheduledPickupAt: string | null
  reason: string | null

  createdBySource: PenaltySource | null
  createdByAdminUserId: number | null
  createdByAdminName: string | null

  settlingTripRequestId: number | null
  settledBookingReference: string | null
  settledPaymobTransactionId: number | null
  reservedAt: string | null
  settledAt: string | null

  waivedByAdminUserId: number | null
  waivedByAdminName: string | null
  waiveReason: string | null
  waivedAt: string | null

  createdAt: string | null
  updatedAt: string | null

  /** True while the amount still counts against the customer. */
  outstanding: boolean
  /** True when an operator could still waive it. */
  waivable: boolean
}

export interface PenaltyFilters extends PageParams {
  search?: string
  status?: string
  source?: string
  userId?: number
}

export interface WaivePenaltyRequest {
  reason: string
}

export interface RefundCreateRequest {
  tripRequestId: number
  /** Minor units. Omitted for a full refund of the remaining balance. */
  amountMinor?: number
  reason: string
  adminNote?: string
  idempotencyKey: string
}

export interface RefundAdminOption {
  id: number
  name: string | null
  email: string | null
}

/* -------------------------------------------------------------- trip edits */

/** The states a customer's change request moves through. */
export const TRIP_EDIT_STATUSES = [
  'PENDING_REVIEW',
  'AWAITING_PAYMENT',
  'PAYMENT_SUBMITTED',
  'APPLIED',
  'REJECTED',
  'WITHDRAWN',
] as const

export type TripEditStatus = (typeof TRIP_EDIT_STATUSES)[number]

/** Which part of the booking an edit touches. */
export const TRIP_EDIT_CHANGES = ['TIME', 'DURATION', 'LOCATION'] as const

export type TripEditChange = (typeof TRIP_EDIT_CHANGES)[number]

/**
 * One customer request to change a booking, as a supervisor sees it.
 *
 * Carries both sides of every change. "Is this worth more money?" cannot be
 * answered from the new value alone — an hour later is nothing, an hour later
 * in a different governorate is a different trip.
 *
 * `previous*` is the snapshot taken when the customer asked; `bookingStartTime`
 * is where the booking stands now. The two can legitimately disagree if the
 * office moved the booking while the request was queued, which is a situation
 * to show rather than hide.
 */
export interface AdminTripEdit {
  id: number
  tripRequestId: number
  tripId: number | null
  bookingReference: string | null
  status: TripEditStatus

  userId: number
  customerName: string | null
  customerPhone: string | null

  changes: TripEditChange[]

  requestedStartTime: string | null
  requestedPackageId: number | null
  requestedPackageName: string | null
  requestedPickupAddress: string | null
  requestedPickupLat: number | null
  requestedPickupLng: number | null
  requestedDropoffAddress: string | null
  requestedDropoffLat: number | null
  requestedDropoffLng: number | null
  customerNote: string | null

  previousStartTime: string | null
  previousEndTime: string | null
  previousPackageName: string | null
  previousPickupAddress: string | null
  previousDropoffAddress: string | null
  previousBasePrice: number | null

  bookingStartTime: string | null
  bookingStatus: string | null
  bookingPaymentMethod: string | null
  bookingPriceMinor: number
  /** Everything already charged for this booking, earlier edits included. */
  bookingTotalMinor: number

  extraAmountMinor: number
  currency: string

  decidedByAdminUserId: number | null
  decidedByAdminName: string | null
  decidedAt: string | null
  decisionNote: string | null

  paymentMethod: string | null
  paymobTransactionId: number | null
  paidAt: string | null
  hasProof: boolean
  proofUploadedAt: string | null

  verifiedByAdminUserId: number | null
  verifiedByAdminName: string | null
  verifiedAt: string | null

  /** True when this edit is waiting on a supervisor to decide it. */
  decidable: boolean
  /** True when this edit is waiting on a supervisor to confirm a transfer. */
  verifiable: boolean

  createdAt: string | null
  updatedAt: string | null
}

export interface TripEditFilters extends PageParams {
  search?: string
  /** A `TripEditStatus`, or `OPEN` for everything still waiting on a human. */
  status?: string
  change?: string
  from?: string
  to?: string
}

/**
 * The supervisor's answer.
 *
 * `extraAmount` is in EGP, which is what the supervisor types — the server
 * converts it once, so no browser has to know about minor units. Omitting it
 * approves the change at the price the booking already carries.
 */
export interface TripEditDecisionRequest {
  approve: boolean
  extraAmount?: number
  note?: string
}

/* --------------------------------------------------------------- mutations */

export interface DriverCreateRequest {
  name: string
  email: string
  phone: string
  password: string
  profileImageUrl?: string
}

export interface MessageResponse {
  message: string
}

export interface UploadResponse {
  url: string
}
